import type { RouterOutputs } from '@/shared/api';

/**
 * 자료실의 축 — 1차 오픈 범위 §3.1 O-02.
 *
 * 유튜브에서 넘어온 사람은 「자료」를 찾아 오는 게 아니라 **「방금 본 영상의 자료」** 를 찾아 온다.
 * 그래서 목록의 1급 개체는 자료가 아니라 영상이고, 자료는 영상에 붙는다.
 *
 * 형태는 손으로 쓰지 않는다 — 서버 AppRouter 출력에서 type-only 로 도출한다 (SUPER-7 D2).
 * 서버 계약이 바뀌면 화면이 컴파일에서 깨진다. 조용히 어긋나는 것보다 낫다.
 */
type LibOutputs = RouterOutputs['lib'];

export type Video = LibOutputs['listVideos']['items'][number];
export type VideoChapter = Video['chapters'][number];

/**
 * 썸네일 주소와 챕터 시점 표기는 계약(packages/contracts)의 것을 그대로 쓴다 — 운영자 화면
 * (등록 폼 미리보기·챕터 편집기)과 같은 구현이어야 두 화면의 표기가 어긋나지 않는다.
 * 여기서 다시 내보내는 이유: 화면은 "영상"의 헬퍼를 한 자리(`@/entities/video`)에서 찾는다.
 */
export { formatTimeSec, youtubeThumbnailUrl } from '@autop/contracts';

/**
 * 시청 링크. `timeSec` 을 주면 그 구간부터 열린다 (LIB-U-05 AC-2) —
 * 챕터의 timeSec 이 곧 `t=` 딥링크의 근거다.
 */
export function youtubeWatchUrl(youtubeId: string, timeSec?: number): string {
  const base = `https://www.youtube.com/watch?v=${youtubeId}`;
  return timeSec !== undefined && timeSec > 0 ? `${base}&t=${Math.trunc(timeSec)}s` : base;
}

/** 개인정보 보호 강화 도메인. 시청 기록이 쌓이지 않는다 */
export function youtubeEmbedUrl(youtubeId: string): string {
  return `https://www.youtube-nocookie.com/embed/${youtubeId}`;
}

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * ISO 게시일 → "2026.07.29" 표기.
 * UTC 기준으로 자른다 — 실행 환경 타임존에 따라 날짜가 달라지면 테스트가 흔들린다.
 */
export function formatPublishedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return `${date.getUTCFullYear()}.${pad(date.getUTCMonth() + 1)}.${pad(date.getUTCDate())}`;
}

/** 챕터 스크린샷 프록시 경로 — `hasScreenshot` 인 챕터에만 그린다 (D10). */
export function chapterScreenshotUrl(chapterId: string): string {
  return `/lib/chapters/${chapterId}/screenshot`;
}
